import React from 'react';
import { Flex, Skeleton, theme } from 'antd';

type SearchPerformanceTableSkeletonProps = {
  // Rows to reserve — pass the table's page size so a full first page is covered
  // and the swap to the real table does not shift the sections below it.
  rowCount: number;
  // Header lines to reserve; antd wraps a column title to as many lines as it
  // needs, so a table with long titles sits taller.
  headerRows?: number;
};

/**
 * A table-shaped loading placeholder for the Performance-by-path and Pages
 * tables: a header row, `rowCount` body rows at the `size="middle"` row height,
 * and a pagination footer.
 */
function SearchPerformanceTableSkeleton({ rowCount, headerRows = 1 }: SearchPerformanceTableSkeletonProps) {
  const { token } = theme.useToken();

  // No token exposes antd's `size="middle"` row/header heights; rebuild them from
  // the text line box and cell padding. Measured in-browser: a body row ≈ 49px,
  // a single header line ≈ 47px.
  const textLine = token.fontSize * token.lineHeight;
  // FIXME: no exact token — the ~3px antd rounds a size="middle" row up by.
  const rowSlack = token.lineWidth * 3;
  const rowHeight = Math.round(textLine + token.paddingSM * 2) + rowSlack;
  const headerHeight = Math.round(textLine * headerRows + token.paddingSM * 2) + rowSlack;

  const line = (key: React.Key, height: number, width: React.CSSProperties['width']) => (
    <Flex key={key} align="center" style={{ height, borderBottom: `${token.lineWidth}px solid ${token.colorSplit}` }}>
      <div style={{ width }}>
        <Skeleton.Input active block size="small" />
      </div>
    </Flex>
  );

  return (
    <div role="status" aria-busy aria-label="Loading table">
      {line('header', headerHeight, '20%')}
      {Array.from({ length: rowCount }, (_, index) => line(index, rowHeight, '60%'))}

      <Flex justify="flex-end" align="center" gap={token.marginXS} style={{ height: token.controlHeight, marginTop: token.margin }}>
        <Skeleton.Button active size="small" />
        <Skeleton.Button active size="small" />
        <Skeleton.Button active size="small" />
      </Flex>
    </div>
  );
}

export default SearchPerformanceTableSkeleton;
